import { Injectable } from '@angular/core';
import { ApiClientService } from '@core/services/api-client.service';
import { Observable } from 'rxjs';
import { formatDateFromDate, formatDateTimeFromDate } from '@shared/utils/date-format';

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  constructor(private apiClient: ApiClientService) {
  }

  /**
   * Get Sendlane Postscript data
   */
  getSendlanePostscript(data: { 
    retryFailed?: number; 
    retryId?: number;
    fromDate?: string; 
    toDate?: string; 
    per_page: number;
    page?: number;
  }): Observable<any> {
    return this.apiClient.get('api/orders/getSendlanePostscript', {
      params: data,
      showLoading: true
    });
  }

  /**
   * Get states by country ID
   */
  getStates(data: { CountryID: any }): Observable<any> {
    return this.apiClient.get('api/system/getStates', {
      params: data,
      showLoading: false,
      cache: { enabled: true, ttl: 300000 } // Cache for 5 minutes
    });
  }

  /**
   * Get all countries
   */
  getCountries(): Observable<any> {
    return this.apiClient.get('api/system/getCountries', {
      showLoading: false,
      cache: { enabled: true, ttl: 600000 } // Cache for 10 minutes
    });
  }

  /**
   * Get cities by state ID
   */
  getCities(data: { StateID: any }): Observable<any> {
    return this.apiClient.get('api/system/cities', {
      params: data,
      showLoading: false,
      cache: { enabled: true, ttl: 300000 } // Cache for 5 minutes
    });
  }

  /**
   * Get timezones
   */
  getTimezones(): Observable<any> {
    return this.apiClient.get('api/system/timezones', {
      showLoading: false,
      cache: { enabled: true, ttl: 86400000 } // Cache for 24 hours
    });
  }

    formatDate(date: string | null | undefined, formatType?: string): string {
    if (!date) {
      return "Not Available";
    }
    const dateObj = date.endsWith("Z") ? new Date(date) : new Date(date + "Z");
    if (isNaN(dateObj.getTime())) {
      return "Not Available";
    }

    if (formatType === 'date') {
      return formatDateFromDate(dateObj, true);
    }

    return formatDateTimeFromDate(dateObj, true);
  }

  formatToLocalDateTime(date: string | Date | null | undefined, formatType: 'date' | 'time' | 'dateTime' = 'dateTime'): string {
    if (!date) {
        return "Not Available";
    }

    // Convert to Date object if it's a string
    let dateObj: Date;
    if (typeof date === 'string') {
        dateObj = date.endsWith('Z') ? new Date(date) : new Date(date + 'Z');
    } else if (date instanceof Date) {
        dateObj = date;
    } else {
        return "Not Available";
    }

    // Check for an invalid date
    if (isNaN(dateObj.getTime())) {
        return "Not Available";
    }

    if (formatType === 'time') {
      const hours = dateObj.getHours();
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      const hour12 = hours % 12 || 12;
      const ampm = hours >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minutes} ${ampm}`;
    }

    if (formatType === 'date') {
      return formatDateFromDate(dateObj);
    }

    return formatDateTimeFromDate(dateObj);
}

  /**
   * Highlight matched text in a string by wrapping it with a span
   * @param text - The full text to search in
   * @param searchTerm - The term to highlight
   * @returns HTML string with highlighted matches
   */
  highlightMatchedText(text: string | number, searchTerm: string): string {
    if (!text || !searchTerm) {
      return String(text || '');
    }

    const textStr = String(text);
    const searchStr = String(searchTerm).trim();

    if (!searchStr) {
      return textStr;
    }

    // Escape special regex characters in search term
    const escapedSearch = searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create regex with global and case-insensitive flags
    const regex = new RegExp(`(${escapedSearch})`, 'gi');

    // Replace matched text with highlighted version
    return textStr.replace(regex, '<span class="highlight-match">$1</span>');
  }

  /**
   * Get currencies
   */
  getCurrencies(): Observable<any> {
    return this.apiClient.get('api/system/currencies', {
      showLoading: false,
      cache: { enabled: true, ttl: 86400000 } // Cache for 24 hours
    });
  }

  /**
   * Get languages
   */
  getLanguages(): Observable<any> {
    return this.apiClient.get('api/system/languages', {
      showLoading: false,
      cache: { enabled: true, ttl: 86400000 } // Cache for 24 hours
    });
  }

  /**
   * Get system settings
   */
  getSystemSettings(): Observable<any> {
    return this.apiClient.get('api/system/system-settings', {
      showLoading: false,
      cache: { enabled: true, ttl: 300000 } // Cache for 5 minutes
    });
  }

  /**
   * Get user preferences
   */
  getUserPreferences(): Observable<any> {
    return this.apiClient.get('api/system/user-preferences', {
      showLoading: false,
      cache: { enabled: true, ttl: 300000 } // Cache for 5 minutes
    });
  }

  /**
   * Update user preferences
   */
  updateUserPreferences(preferences: any): Observable<any> {
    return this.apiClient.put('api/system/user-preferences', preferences, {
      showLoading: true
    });
  }

  /**
   * Get notification settings
   */
  getNotificationSettings(): Observable<any> {
    return this.apiClient.get('api/system/notification-settings', {
      showLoading: false,
      cache: { enabled: true, ttl: 300000 } // Cache for 5 minutes
    });
  }

  /**
   * Update notification settings
   */
  updateNotificationSettings(settings: any): Observable<any> {
    return this.apiClient.put('api/system/notification-settings', settings, {
      showLoading: true
    });
  }

  /**
   * Get help documentation
   */
  getHelpDocumentation(): Observable<any> {
    return this.apiClient.get('api/system/help-documentation', {
      showLoading: false,
      cache: { enabled: true, ttl: 3600000 } // Cache for 1 hour
    });
  }

  /**
   * Get system health status
   */
  getSystemHealth(): Observable<any> {
    return this.apiClient.get('api/system/system-health', {
      showLoading: false,
      cache: { enabled: true, ttl: 60000 } // Cache for 1 minute
    });
  }

  /**
   * Clear cache for common endpoints
   */
  clearCommonCache(): void {
    this.apiClient.clearCacheForEndpoint('api/system');
  }
}
